import _ from '@/assets/lib/my-lodash'

type UnknownRecord = Record<string, any>

type AddBackupParams = {
  label: string
  type?: string
  yagiofferFlag?: boolean
  formatId?: string
  appHtml?: string
  backupKeys?: string[]
}

type FirebaseBackupClient = {
  isAddingBackupNow: boolean
  isAuthenticated: boolean
  $message: (payload: { message: string, type: string }) => void
  $iterateStorage: (
    iteratee: (value: any, key: string, iterationNumber: number) => void,
    callback: (err: unknown) => void
  ) => Promise<void>
  $deviceLib: { isMobileDevice: boolean }
  user: { uid: string, email: string, emailVerified: boolean }
  $globalVar: UnknownRecord
  reloadToken: () => Promise<void>
  accessToken: string
  config: { apiKey: string }
  $site: { appFlag: string }
  $axios: { post: (url: string, data: FormData, config?: { timeout: number }) => Promise<any> }
  $varuna: {
    endPoints: {
      sendDataRoxx: string
      saveBackupdata: string
    }
  }
}

const DEFAULT_BACKUP_KEYS = [
  'format_rirekisho',
  'format_shokumukeirekisho',
  'format_letter',
  'format_etc',
  'format_taishokutodoke',
  'basicData',
  'recentEdit',
  'lastSaveDate'
]

export async function addBackup (vm: FirebaseBackupClient | null, params: AddBackupParams) {
  if (!vm) {
    console.error('[useFirebaseAddBackup] firebase client is not initialized')
    return
  }
  const {
    label,
    type = 'user',
    yagiofferFlag = false,
    formatId = '',
    appHtml = '',
    backupKeys = DEFAULT_BACKUP_KEYS
  } = params

  vm.isAddingBackupNow = true
  if (!vm.isAuthenticated) {
    vm.$message({
      message: 'ログインしてください',
      type: 'error'
    })
    return
  }

  const storageData: UnknownRecord = {}
  await vm.$iterateStorage(
    function (value, key) {
      if (_.includes(backupKeys, key)) {
        storageData[key] = value
      }
    },
    function (err) {
      if (err) {
        console.error(err)
      }
    }
  )

  if (Object.keys(storageData).length === 0) {
    vm.$message({
      message: 'データがありません',
      type: 'error'
    })
    vm.isAddingBackupNow = false
    return
  }

  storageData._sync_meta = {
    device: vm.$deviceLib.isMobileDevice ? 'SP' : 'PC',
    timestamp: new Date().toISOString(),
    device_id: localStorage.getItem('_device_id')
  }

  try {
    const formData = new FormData()
    formData.append('uuid', vm.user.uid)
    formData.append('email', vm.user.email)
    formData.append('backup_uid', vm.user.uid)
    formData.append('site_flag', 'r')
    formData.append('backup_type', type)
    formData.append('backup_name', label)
    formData.append('version', '2')
    formData.append('yagioffer_flag', yagiofferFlag ? 'true' : 'false')
    const _bdata = JSON.stringify(storageData)
    formData.append('storage_data', _bdata)

    if (formatId.includes('shokumukeirekisho')) {
      formData.append('last_keirekisho_html', appHtml)
    } else if (formatId.includes('rerekisho') || formatId.includes('rirekisho')) {
      formData.append('last_rirekisho_html', appHtml)
    } else {
      formData.append('last_etc_html', appHtml)
    }

    formData.append('device_flag', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('email_verified', vm.user.emailVerified ? '1' : '0')

    if (vm.$globalVar.isAutoRT4Rtable) {
      const autoRTInfo = vm.$globalVar.autoRTInfo
      const resolvedPermissionType = Number.isFinite(Number(autoRTInfo.permissionType))
        ? Number(autoRTInfo.permissionType)
        : Number(autoRTInfo.oldPermissionType)
      if (autoRTInfo.clientId === 'roxx_api' && autoRTInfo.permissionType === 10) {
        formData.append('roxx_10_flag', '1')
      } else {
        formData.append('roxx_10_flag', '0')
      }

      if (autoRTInfo.clientId === 'roxx_api' && [17, 18, 19, 20].includes(autoRTInfo.permissionType)) {
        const roxxPermType = autoRTInfo.permissionType
        try {
          await vm.reloadToken()
          if (!vm.accessToken) {
            console.warn('[useFirebaseAddBackup] sendDataRoxx skipped: accessToken not found')
          } else {
            const roxxFormData = new FormData()
            roxxFormData.append('token', vm.accessToken)
            roxxFormData.append('apikey', vm.config.apiKey)
            roxxFormData.append('uuid', vm.user.uid)
            roxxFormData.append('format_id', formatId)
            roxxFormData.append('filename', 'rirekisho_pdf.pdf')
            roxxFormData.append('app_html', appHtml)
            roxxFormData.append('permission_type', String(roxxPermType))
            roxxFormData.append('dialog_name', 'auto_rt')
            roxxFormData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
            roxxFormData.append('app_flag', vm.$site.appFlag)
            roxxFormData.append('reqflg', '2')
            roxxFormData.append('version', '2')
            await vm.$axios.post(vm.$varuna.endPoints.sendDataRoxx, roxxFormData, { timeout: 60000 })
          }
        } catch (error) {
          console.error('[useFirebaseAddBackup] sendDataRoxx failed', error)
        }
      } else {
        formData.append('auto_rt_flag', '1')
        formData.append('auto_rt_id', autoRTInfo.clientId)
        if (Number.isFinite(resolvedPermissionType)) {
          formData.append('auto_rt_type', String(resolvedPermissionType))
        }
      }
      vm.$globalVar.$eventHub.$emit(
        'update_rt_condition',
        autoRTInfo.clientId,
        resolvedPermissionType,
        true,
        autoRTInfo.dialogName || 'auto_rt'
      )
    } else {
      formData.append('roxx_10_flag', '0')
    }

    formData.append('dialog_name', 'auto_rt_dialog')
    formData.append('format_id', formatId)

    await vm.$axios.post(vm.$varuna.endPoints.saveBackupdata, formData, { timeout: 60000 })
    vm.$globalVar.initAutoRT()

    vm.$message({
      type: 'success',
      message: '保存成功:' + label
    })
    vm.isAddingBackupNow = false
  } catch (error) {
    console.error(error)
    vm.$message({
      type: 'error',
      message: '保存失敗:' + label
    })
    vm.isAddingBackupNow = false
  }
}
